import { type Session } from '@ory/client-fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { config, ory } from '@/lib'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>()
  const legacyStore = useStore()
  const sessionData = ref<null | Session>(null)

  const authorization = computed(() => {
    if (!token.value) {
      return undefined
    }

    return `Bearer ${token.value}`
  })

  const setToken = (value?: string) => {
    token.value = value
  }

  const setSessionData = (data: Session) => {
    sessionData.value = data
  }

  const isMetapicUserCreated = computed(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      return true
    }
    const metadataPublic = sessionData.value?.identity?.metadata_public
    return !!metadataPublic && 'metapic_user_id' in metadataPublic
  })

  const authenticate = async () => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      setToken('demo-token')
      await legacyStore.dispatch('user/setTokenFromAuthPinia', authorization.value, { root: true })
      return true
    }

    try {
      const data = await ory.toSession({ tokenizeAs: 'jwt_default' })
      setSessionData(data)
      setToken(data.tokenized)
    } catch {
      setToken(undefined)
    }

    await legacyStore.dispatch('user/setTokenFromAuthPinia', authorization.value, { root: true })
    return !!token.value
  }

  let refreshPromise: Promise<string | undefined> | null = null

  const refreshToken = (): Promise<string | undefined> => {
    if (refreshPromise === null) {
      refreshPromise = authenticate()
        .then(() => token.value)
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  const logout = async () => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      setToken(undefined)
      return null
    }

    const identityAppLogin = new URL('/auth/login', config.identityAppUrl)
    identityAppLogin.searchParams.append('app', 'advertiser')
    identityAppLogin.searchParams.append('returnUrl', window.origin)

    try {
      setToken(undefined)

      const data = await ory.createBrowserLogoutFlow({ returnTo: identityAppLogin.toString() })
      return data.logout_url
    } catch {
      return null
    }
  }

  const handleRefreshFailure = () => {
    window.location.href = '/logout'
  }

  return {
    authorization,
    authenticate,
    refreshToken,
    handleRefreshFailure,
    logout,
    isMetapicUserCreated,
  }
})
